import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { Flame, TrendingUp, Calendar, Zap, ChevronDown } from 'lucide-react';

const PERIOD_OPTIONS = [
  { value: 7, label: '7 jours' },
  { value: 14, label: '14 jours' },
  { value: 30, label: '30 jours' },
];

export function CalorieHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [showPeriod, setShowPeriod] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await apiClient.get(`/training/history?days=${days}`);
      setData(res);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { history, stats } = data;
  const chartData = history.map(h => ({
    ...h,
    label: h.day_num,
    shortDate: `${h.day} ${h.day_num}`,
  }));

  return (
    <div className="space-y-3" data-testid="calorie-history-chart">
      {/* Header + Period selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#FF6B35]" />
          <h3 className="text-sm font-medium text-zinc-400">Historique depense</h3>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowPeriod(!showPeriod)}
            className="flex items-center gap-1 text-xs text-zinc-400 bg-[#0F0F0F] border border-[#2A2A2E] rounded-sm px-2.5 py-1.5 hover:border-zinc-600 transition-colors"
            data-testid="period-selector"
          >
            <Calendar className="w-3 h-3" />
            {PERIOD_OPTIONS.find(p => p.value === days)?.label}
            <ChevronDown className="w-3 h-3" />
          </button>
          {showPeriod && (
            <div className="absolute right-0 top-full mt-1 bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm shadow-lg z-10 overflow-hidden">
              {PERIOD_OPTIONS.map(p => (
                <button
                  key={p.value}
                  onClick={() => { setDays(p.value); setShowPeriod(false); }}
                  className={`block w-full text-left px-4 py-2 text-xs hover:bg-[#2A2A2E] transition-colors ${
                    days === p.value ? 'text-[#FF6B35] font-bold' : 'text-zinc-400'
                  }`}
                  data-testid={`period-${p.value}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-2">
        <StatCard icon={Flame} label="Total" value={`${stats.total_calories}`} unit="kcal" color="#FF6B35" />
        <StatCard icon={Zap} label="Moy/seance" value={`${stats.avg_per_session}`} unit="kcal" color="#FFB800" />
        <StatCard icon={Calendar} label="Jours actifs" value={`${stats.active_days}`} unit={`/${stats.days}`} color="#22c55e" />
        <StatCard icon={TrendingUp} label="Record" value={`${stats.max_day}`} unit="kcal" color="#ef4444" />
      </div>

      {/* Chart */}
      <div className="bg-[#0F0F0F] rounded-sm p-3 pt-4">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fill: '#71717a' }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
              interval={days <= 7 ? 0 : days <= 14 ? 1 : 'preserveStartEnd'}
            />
            <YAxis
              tick={{ fontSize: 9, fill: '#71717a' }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,107,53,0.08)' }} />
            {stats.avg_per_session > 0 && (
              <ReferenceLine
                y={stats.avg_daily}
                stroke="#FF6B35"
                strokeDasharray="4 4"
                strokeOpacity={0.5}
              />
            )}
            <Bar dataKey="calories" radius={[3, 3, 0, 0]} maxBarSize={days <= 7 ? 40 : days <= 14 ? 24 : 14}>
              {chartData.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.is_rest && entry.calories === 0 ? '#27272a' : entry.calories >= stats.avg_per_session ? '#FF6B35' : '#FF6B3580'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-center gap-4 mt-2">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#FF6B35]" />
            <span className="text-[9px] text-zinc-500">Seance</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#27272a]" />
            <span className="text-[9px] text-zinc-500">Repos</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-0 border-t border-dashed border-[#FF6B35]/50" />
            <span className="text-[9px] text-zinc-500">Moyenne</span>
          </div>
        </div>
      </div>

      {/* Daily breakdown (compact) */}
      <details className="group">
        <summary className="flex items-center gap-2 text-xs text-zinc-500 cursor-pointer hover:text-zinc-300 transition-colors py-1" data-testid="history-details-toggle">
          <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
          Detail jour par jour
        </summary>
        <div className="mt-2 max-h-[300px] overflow-y-auto space-y-0.5">
          {[...history].reverse().map(h => (
            <div
              key={h.date}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs ${
                h.calories > 0 && !h.is_rest ? 'bg-[#FF6B35]/5' : ''
              }`}
            >
              <span className="text-zinc-600 w-16 font-mono">{h.day} {h.day_num}</span>
              <span className="flex-1 text-zinc-400 truncate">{h.session || 'Repos'}</span>
              <span className={`font-mono font-bold ${h.calories > 0 ? 'text-[#FF6B35]' : 'text-zinc-700'}`}>
                {h.calories > 0 ? `${h.calories} kcal` : '—'}
              </span>
              {h.manual && <span className="text-[8px] text-[#FFB800]">M</span>}
              {h.extras > 0 && <span className="text-[8px] text-blue-400">+{h.extras}</span>}
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, unit, color }) {
  return (
    <div className="bg-[#0F0F0F] rounded-sm p-2.5 text-center" style={{ borderTop: `2px solid ${color}` }}>
      <Icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color }} />
      <p className="font-mono text-base font-bold text-white leading-none">{value}</p>
      <p className="text-[9px] text-zinc-500 mt-0.5">{unit}</p>
      <p className="text-[8px] text-zinc-600">{label}</p>
    </div>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm px-3 py-2 shadow-lg">
      <p className="text-[10px] text-zinc-400">{d.shortDate} · {d.date}</p>
      <p className="font-mono text-sm font-bold text-white">{d.calories} kcal</p>
      {d.session && <p className="text-[10px] text-[#FF6B35]">{d.session}</p>}
      {d.manual && <p className="text-[9px] text-[#FFB800]">Ajuste manuellement</p>}
    </div>
  );
}
